import React, { useState } from 'react';
import type { RadioButtonProps, UniqueRadioButtonValues } from '#/types/inputs';
import Fieldset from '#/shared/components/Fieldset';
import { type StringIdOrUntranslatedEnglishString } from '#/shared/components/FormattedMessageWithEnglishFallback';
import RadioButton from './RadioButton';
import * as Styled from './styled';

type RadioGroupLabel =
  // groupLabel is the title of the group of radio buttons
  // In most cases one should be provided, but in some case (eg Map Settings) we provide a custom one
  | {
      groupLabel: StringIdOrUntranslatedEnglishString;
      customFieldsetProvided?: never;
    }
  | { groupLabel?: never; customFieldsetProvided: true };

interface BaseRadioGroupProps
  extends Omit<
    RadioButtonProps,
    'label' | 'dataGtm' | 'checked' | 'value' | 'description'
  > {
  radioButtons: UniqueRadioButtonValues[];
  // If a radio button should be checked by default, the value of that button
  initialValue?: string;
}

type RadioGroupProps = RadioGroupLabel & BaseRadioGroupProps;

function BaseRadioGroup({
  name,
  radioButtons,
  onChange,
  initialValue = undefined,
  labelPosition,
}: BaseRadioGroupProps) {
  const [currentValue, setCurrentValue] = useState(initialValue);
  const valueChanged = (value: string) => {
    onChange(value);
    setCurrentValue(value);
  };

  return (
    <Styled.RadioGroupWrapper>
      {radioButtons.map(({ value, label, dataGtm, description }) => {
        return (
          <RadioButton
            key={value}
            name={name}
            value={value}
            checked={value === currentValue}
            label={label}
            onChange={valueChanged}
            dataGtm={dataGtm}
            labelPosition={labelPosition}
            description={description}
          />
        );
      })}
    </Styled.RadioGroupWrapper>
  );
}

function RadioGroup({
  groupLabel,
  name,
  radioButtons,
  onChange,
  initialValue = undefined,
  labelPosition,
  customFieldsetProvided,
  className,
}: RadioGroupProps): React.ReactElement {
  const radioGroup = (
    <BaseRadioGroup
      name={name}
      radioButtons={radioButtons}
      onChange={onChange}
      initialValue={initialValue}
      labelPosition={labelPosition}
    />
  );

  if (customFieldsetProvided) {
    return <div className={className}>{radioGroup}</div>;
  }

  return (
    <Fieldset headingTag="h2" groupLabel={groupLabel} className={className}>
      {radioGroup}
    </Fieldset>
  );
}

export default RadioGroup;
